/**
 * AdminRoute — DB-backed gate for /admin routes.
 *
 * Uses the same public.is_admin() RPC as Supabase RLS policies so frontend
 * route gating and database access share one source of truth.
 *
 * - Not authenticated → redirect to /login (delegates to ProtectedRoute)
 * - Authenticated but not the enabled founder user ID → 404 (not 403)
 * - Authenticated founder → renders children
 */

import React, { lazy, Suspense, useEffect, useState } from "react";
import { useAuthContext } from "../../../contexts/AuthContext";
import { supabase } from "../../../lib/supabase";
import { ProtectedRoute } from "./ProtectedRoute";

const NotFoundPage = lazy(() =>
  import("../NotFoundPage").then((m) => ({ default: m.NotFoundPage }))
);

function AdminGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuthContext();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function checkAdmin() {
      if (!user) {
        setIsAdmin(false);
        return;
      }

      const { data, error } = await supabase.rpc("is_admin");
      if (cancelled) return;
      setIsAdmin(!error && data === true);
    }

    setIsAdmin(null);
    void checkAdmin();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  if (isAdmin == null) {
    return (
      <div className="min-h-screen bg-fp-slate-100 flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-2 border-fp-emerald-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <Suspense fallback={null}>
        <NotFoundPage onNavigateHome={() => { window.location.href = "/"; }} />
      </Suspense>
    );
  }

  return <>{children}</>;
}

/**
 * Full admin route wrapper: auth check first, then founder user-ID check.
 */
export function AdminRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <AdminGate>{children}</AdminGate>
    </ProtectedRoute>
  );
}
